/* global closest:false */
/* global matches:false */
/* global trigger:false */



/**
 * Validator inspired by Zurb Foundation Abide
 *
 * https://github.com/foundation/foundation-sites/blob/develop/js/foundation.abide.js
 */

class FormValidator {
	/**
	 * Creates a new instance of FormValidator.
	 * @class
	 * @fires FormValidator#init
	 * @param {Object} element - from element to add the trigger to.
	 * @param {Object} options - Overrides to the default plugin settings.
	 */
	constructor(element, options = {}) {
		this.formElement = element;
		this.options = Object.assign({}, FormValidator.defaults, options);

		this.resetForm = this.resetForm.bind(this);
		this.validateForm = this.validateForm.bind(this);

		this._init();

		trigger(this.formElement, 'forminit', this);
	}



	/**
	 * Initializes the FormValidator plugin and calls functions to get FormValidator functioning on load.
	 * @private
	 */
	_init() {
		this.inputElements = this.formElement.querySelectorAll('input, textarea, select');

		this._events();
	}



	/**
	 * Initializes events for FormValidator
	 * @private
	 */
	_events() {
		this.formElement.addEventListener('reset', this.resetForm);
		this.formElement.addEventListener('submit', this.validateForm);

		Array.prototype.forEach.call(this.inputElements, (inputElement) => {
			inputElement.addEventListener('change', (e) => {
				this.validateInput(e.target);
			});
		});
	}



	/**
	 * Checks whether or not a form element has the required attribute and if it's checked or not
	 * @param {Object} element - object to check for required attribute
	 * @returns {Boolean} Boolean value depends on whether or not attribute is checked or empty
	 */
	requiredCheck(element) {
		if (!element.hasAttribute('required')) {
			return true;
		}


  
		var isGood = true;
  
		switch (element.type) {
			case 'checkbox':
				isGood = element.checked;
				break;

			case 'select':
			case 'select-one':
			case 'select-multiple':
				var opt = element.querySelector('option[selected]');
				if (!opt.length || !opt.value) {
					isGood = false;
				}
				break;

			default:
				if (!element.value || !element.value.length) {
					isGood = false;
				}
		}
  
		return isGood;
	}



	/**
	 * Based on element, get the first element with selector in this order:
	 * 1. The element's direct sibling('s).
	 * 2. The element's parent's children.
	 *
	 * This allows for multiple form errors per input, though if none are found, no form errors will be shown.
	 *
	 * @param {Object} element - object to use as reference to find the form error selector.
	 * @returns {Object} object with the selector.
	 */
	findFormErrors(element) {
		const parent = element.parentNode;

		var error = [].slice.call(parent.children).filter((child) => {
			return child !== element && matches(child, this.options.formErrorSelector);
		});

		if (!error.length) {
			error = parent.querySelectorAll(this.options.formErrorSelector);
		}

		return error;
	}



	/**
	 * Get the first element in this order:
	 * 2. The <label> with the attribute `[for="someInputId"]`
	 * 3. The `.closest()` <label>
	 *
	 * @param {Object} element - object to check for required attribute
	 * @returns {Boolean} Boolean value depends on whether or not attribute is checked or empty
	 */
	findLabel(element) {
		var id = element.id;
		var label = this.formElement.querySelector(`label[for="${id}"]`);

		if (!label) {
			return closest(element, 'label');
		}

		return label;
	}



	/**
	 * Reset (remove) accessibility properties to form error message
	 * @param {Object} element - object to remove accessibility properties
	 */
	resetAccessibleAlert(formError) {
		formError.setAttribute('aria-hidden', 'true');
		formError.setAttribute('role', 'none');
		formError.setAttribute('aria-live', 'off');
	}



	/**
	 * Add accessibility properties to form error message
	 * @param {Object} element - object to add accessibility properties
	 */
	setAccessibleAlert(formError) {
		formError.setAttribute('aria-hidden', 'false');
		formError.setAttribute('role', 'alert');
		formError.setAttribute('aria-live', 'polite');
	}



	/**
	 * Adds the CSS error class as specified by the FormValidator settings to the label, input, and the form
	 * @param {Object} element - object to add the class to
	 */
	addErrorClasses(element) {
		var label = this.findLabel(element);
		var formErrors = this.findFormErrors(element);

		if (label) {
			label.classList.add(this.options.labelErrorClass);
		}

		if (formErrors.length) {
			Array.prototype.forEach.call(formErrors, (formError) => {
				this.setAccessibleAlert(formError);

				// when error is already visible we need to announce that again (for screen readers)
				// so we will hide error message for a while and reveal it again
				if (formError.getAttribute('aria-hidden') == 'false') {
					formError.classList.remove(this.options.formErrorClass);
					setTimeout(() => {
						formError.classList.add(this.options.formErrorClass);
					}, 0);
				} else {
					formError.classList.add(this.options.formErrorClass);
				}
			});
		}

		element.classList.add(this.options.inputErrorClass);
		element.setAttribute('data-invalid', '');
		element.setAttribute('aria-invalid', 'true');
	}



	/**
	 * Remove CSS error classes etc from an entire radio button group
	 * @param {String} groupName - A string that specifies the name of a radio button group
	 *
	 */
	removeRadioErrorClasses(groupName) {
		var els = this.formElement.querySelectorAll(`[type="radio"][name="${groupName}"]`);

		Array.prototype.forEach.call(els, (element) => {
			var label = this.findLabel(element);
			var formErrors = this.findFormErrors(element);

			if (label) {
				label.classList.remove(this.options.labelErrorClass);
			}

			if (formErrors.length) {
				Array.prototype.forEach.call(formErrors, (formError) => {
					formError.classList.remove(this.options.formErrorClass);
					this.resetAccessibleAlert(formError);
				});
			}

			element.classList.remove(this.options.inputErrorClass);
			element.removeAttribute('data-invalid');
			element.setAttribute('aria-invalid', 'false');
		});
	}



	/**
	 * Removes CSS error class as specified by the FormValidator settings from the label, input, and the form
	 * @param {Object} element - object to remove the class from
	 */
	removeErrorClasses(element) {
		// radios need to clear all of the els
		if (element.type == 'radio') {
			return this.removeRadioErrorClasses(element.getAttribute('name'));
		}

		var label = this.findLabel(element);
		var formErrors = this.findFormErrors(element);

		if (label) {
			label.classList.remove(this.options.labelErrorClass);
		}

		if (formErrors.length) {
			Array.prototype.forEach.call(formErrors, (formError) => {
				formError.classList.remove(this.options.formErrorClass);
				this.resetAccessibleAlert(formError);
			});
		}

		element.classList.remove(this.options.inputErrorClass);
		element.removeAttribute('data-invalid');
		element.setAttribute('aria-invalid', 'false');
	}



	/**
	 * Goes through a form to find inputs and proceeds to validate them in ways specific to their type
	 * @fires FormValidator#elementinvalid
	 * @fires FormValidator#elementvalid
	 * @param {Object} element - element to validate, should be an HTML input
	 * @returns {Boolean} goodToGo - If the input is valid or not.
	 */
	validateInput(element) {
		var clearRequire = this.requiredCheck(element);
		var validated = false;
		var customValidator = true;
		var validator = element.getAttribute('data-validator');

		// don't validate ignored inputs or hidden inputs
		if (element.hasAttribute('data-validate-ignore') || element.type == 'hidden') {
			return true;
		}

		switch (element.type) {
			case 'radio':
			  validated = this.validateRadio(element.getAttribute('name'));
			  break;

			case 'checkbox':
			  validated = clearRequire;
			  break;

			case 'select':
			case 'select-one':
			case 'select-multiple':
			  validated = clearRequire;
			  break;

			default:
			  validated = this.validateText(element);
		}

		if (validator) {
			customValidator = this.matchValidation(element, validator, element.hasAttribute('required'));
		}

		var goodToGo = [clearRequire, validated, customValidator].indexOf(false) === -1;

		var message = (goodToGo ? 'elementvalid' : 'elementinvalid');

		this[goodToGo ? 'removeErrorClasses' : 'addErrorClasses'](element);

		// Fires when the input is done checking for validation. Event trigger is either `elementvalid` or `elementinvalid`
		// Trigger includes the DOM element of the input.
		trigger(element, message, this);
		trigger(this.formElement, message, {
			element: element,
			validator: this,
		});

		return goodToGo;
	}



	/**
	 * Goes through a form and if there are any invalid inputs, it will display the form error element
	 * @returns {Boolean} noError - true if no errors were detected...
	 * @fires FormValidator#formvalid
	 * @fires FormValidator#forminvalid
	 */
	validateForm() {
		const formEl = this.formElement;
		var acc = [];

		Array.prototype.forEach.call(this.inputElements, (inputElement) => {
			acc.push(this.validateInput(inputElement));
		});

		var noError = acc.indexOf(false) === -1;

		const customErrors = formEl.querySelectorAll('[data-validate-error]');
		for (let i = 0; i < customErrors.length; i++) {
			customErrors[i].style.display = noError ? 'none' : 'block';
		}

		// Fires when the form is finished validating. Event trigger is either `formvalid` or `forminvalid`.
		// Trigger includes the element of the form.
		if (noError) {
			trigger(formEl, 'formvalid', this);
		} else {
			trigger(formEl, 'forminvalid', this);
		}

		return noError;
	}



	/**
	 * Determines whether or a not a text input is valid based on the pattern specified in the attribute.
	 * If no matching pattern is found, returns true.
	 * @param {Object} element - object to validate, should be a text input HTML element
	 * @param {String} pattern - string value of one of the RegEx patterns in FormValidator.options.patterns
	 * @returns {Boolean} Boolean value depends on whether or not the input value matches the pattern specified
	 */
	validateText(element, pattern) {
		// A pattern can be passed to this function, or it will be infered from the input's "pattern" attribute, or it's "type" attribute
		pattern = (pattern || element.getAttribute('pattern') || element.getAttribute('type'));
		var inputText = element.value;
		var valid = false;

		if (inputText.length) {
			// If the pattern attribute on the element is in FormValidator's list of patterns, then test that regexp
			if (this.options.patterns.hasOwnProperty(pattern)) {
				valid = this.options.patterns[pattern].test(inputText);
			}
			// If the pattern name isn't also the type attribute of the field, then test it as a regexp
			else if (pattern !== element.getAttribute('type')) {
				valid = new RegExp(pattern).test(inputText);
			}
			else {
				valid = true;
			}
		}
		// An empty field is valid if it's not required
		else if (!element.hasAttribute('required')) {
			valid = true;
		}

		return valid;
	}



	/**
	 * Determines whether or a not a radio input is valid based on whether or not it is required and selected.
	 * Although the function targets a single `<input>`, it validates by checking the `required` and `checked`
	 * properties of all radio buttons in its group.
	 * @param {String} groupName - A string that specifies the name of a radio button group
	 * @returns {Boolean} Boolean value depends on whether or not at least one radio input has been selected (if it's required)
	 */
	validateRadio(groupName) {
  		// If at least one radio in the group has the `required` attribute, the group is considered required
		// Per W3C spec, all radio buttons in a group should have `required`, but we're being nice
		var group = this.formElement.querySelectorAll(`[type="radio"][name="${groupName}"]`);
		var valid = false;
		var required = false;

		// For the group to be required, at least one radio needs to be required
		Array.prototype.forEach.call(group, (element) => {
			if (element.hasAttribute('required')) {
				required = true;
			}
		});

		if (!required) {
			valid = true;
		}

		if (!valid) {
			// For the group to be valid, at least one radio needs to be checked
			Array.prototype.forEach.call(group, (element) => {
				if (element.checked) {
					valid = true;
				}
			});
		};

		return valid;
	}



	/**
	 * Determines if a selected input passes a custom validation function. Multiple validations can be used,
	 * if passed to the element with `data-validator="foo bar baz"` in a space separated listed.
	 * @param {Object} element - input element.
	 * @param {String} validators - a string of function names matching functions in the FormValidator.options.validators object.
	 * @param {Boolean} required - self explanatory?
	 * @returns {Boolean} - true if validations passed.
	 */
	matchValidation(element, validators, required) {
		required = required ? true : false;

		var clear = validators.split(' ').map((v) => {
		  	return this.options.validators[v](element, required, element.parentNode);
		});

		return clear.indexOf(false) === -1;
	}



	/**
	 * Resets form inputs and styles
	 * @fires FormValidator#formreset
	 */
	resetForm() {
		const opts = this.options;
		const formEl = this.formElement;

		const allLabels = formEl.querySelectorAll(`.${opts.labelErrorClass}`);
		for (let i = 0; i < allLabels.length; i++) {
			const el = allLabels[i];

			if (el.nodeName.toLowerCase() != 'small') {
				el.classList.remove(opts.labelErrorClass);
			}
		}

		const allInputs = formEl.querySelectorAll(`.${opts.inputErrorClass}`);
		for (let i = 0; i < allInputs.length; i++) {
			const el = allInputs[i];

			if (el.nodeName.toLowerCase() != 'small') {
				el.classList.remove(opts.inputErrorClass);
			}
		}

		const allFormErrors = formEl.querySelectorAll(`${opts.formErrorSelector}.${opts.formErrorClass}`);
		for (let i = 0; i < allFormErrors.length; i++) {
			const el = allFormErrors[i];

			if (el.nodeName.toLowerCase() != 'small') {
				el.classList.remove(opts.formErrorClass);
				this.resetAccessibleAlert(el);
			}
		}

		const customErrors = formEl.querySelectorAll('[data-validate-error]');
		for (let i = 0; i < customErrors.length; i++) {
			customErrors[i].style.display = 'none';
		}

		const inputs = formEl.querySelectorAll('input');
		for (let i = 0; i < inputs.length; i++) {
			const el = inputs[i];

			if (
				!['button', 'submit', 'reset', 'hidden', 'radio', 'checkbox'].includes(el.type) &&
				!el.hasAttribute('data-validate-ignore')
			) {
				el.value = '';
				el.removeAttribute('data-invalid');
				el.setAttribute('aria-invalid', 'false');
			}
		}

		const radiosAndCheckboxes = formEl.querySelectorAll('input[type="radio"], input[type="radio"]');
		for (let i = 0; i < radiosAndCheckboxes.length; i++) {
			const el = radiosAndCheckboxes[i];

			if (!el.hasAttribute('data-validate-ignore')) {
				el.checked = false;
				el.removeAttribute('data-invalid');
				el.setAttribute('aria-invalid', 'false');
			}
		}

		// Fires when the form has been reset.
		trigger(formEl, 'formreset', this);
	}
};



/**
 * Default settings for plugin
 */
FormValidator.defaults = {
	// Class to be applied to input labels on failed validation.
	labelErrorClass: 'is-invalid-label',

	// Class to be applied to inputs on failed validation.
	inputErrorClass: 'is-invalid-input',

	// Class selector to use to target Form Errors for show/hide.
	formErrorSelector: '.form-error',

	// Class added to Form Errors on failed validation.
	formErrorClass: 'is-visible',

	patterns: {
		alpha: /^[a-zA-Z]+$/,
		alpha_numeric: /^[a-zA-Z0-9]+$/,
		integer: /^[-+]?\d+$/,
		number: /^[-+]?\d*(?:[\.\,]\d+)?$/,

		// amex, visa, diners
		card: /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/,
		cvv: /^([0-9]){3,4}$/,

		// http://www.whatwg.org/specs/web-apps/current-work/multipage/states-of-the-type-attribute.html#valid-e-mail-address
		email: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,

		url: /^(https?|ftp|file|ssh):\/\/(((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/,
		// abc.de
		domain: /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,8}$/,

		datetime: /^([0-2][0-9]{3})\-([0-1][0-9])\-([0-3][0-9])T([0-5][0-9])\:([0-5][0-9])\:([0-5][0-9])(Z|([\-\+]([0-1][0-9])\:00))$/,
		// YYYY-MM-DD
		date: /(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))$/,
		// HH:MM:SS
		time: /^(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9]){2}$/,
		dateISO: /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$/,
		// MM/DD/YYYY
		month_day_year: /^(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.]\d{4}$/,
		// DD/MM/YYYY
		day_month_year: /^(0[1-9]|[12][0-9]|3[01])[- \/.](0[1-9]|1[012])[- \/.]\d{4}$/,

		// #FFF or #FFFFFF
		color: /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,

		/**
		 * Acceptable domains names:
		 * test.com
		 * www.test.com
		 * demo.test.com
		 * http://www.test.com
		 * https://demo.test.com
		 *
		 * Invalid domain names:
		 * test.com/index.php
		 */
		url_only: /^((http|https):\/\/){0,1}([a-zA-Z0-9][a-zA-Z0-9-]{0,61}\.){1,}[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](\/){0,1}$/,
	},
};
